// src/components/common/StartHereHint.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { getCustomIcon } from '../../utils/customIcons';

// -- Animation registry --

interface IconSlot {
  delay: string;
  margin: string;
  nudge?: string;
}

interface AnimationConfig {
  iconCount: number;
  iconSlots: IconSlot[];
  fadeKeyframeName: string;
  backdropAnimation: string;
  restartPause: number;
  keyframes: Record<string, Record<string, Record<string, string | number>>>;
  getIconSx: (slot: IconSlot) => SxProps<Theme>;
}

const ANIMATIONS: Record<string, AnimationConfig> = {
  singleIconEntranceRight: {
    iconCount: 1,
    iconSlots: [{ delay: '0s', margin: '0' }],
    fadeKeyframeName: 'hintFadeOut',
    backdropAnimation:
      'hintBackdropIn 0.35s ease-out forwards, hintFadeOut 1.2s ease 2.8s forwards',
    restartPause: 800,
    keyframes: {
      '@keyframes hintPulse': {
        '0%': { transform: 'scale(0.85)', opacity: 0 },
        '100%': { transform: 'scale(1)', opacity: 1 },
      },
      '@keyframes hintNudge': {
        '0%': { transform: 'translateX(0)' },
        '45%': { transform: 'translateX(10px)' },
        '50%': { transform: 'translateX(10px)' },
        '90%': { transform: 'translateX(0)' },
        '100%': { transform: 'translateX(0)' },
      },
      '@keyframes hintFadeOut': {
        '0%': { opacity: 1 },
        '100%': { opacity: 0 },
      },
    },
    getIconSx: () => ({
      animation: `
        hintPulse 0.35s ease-out 1,
        hintNudge 0.8s ease-in-out 3 0.35s,
        hintFadeOut 1.2s ease 2.8s forwards
      `,
    }),
  },

  doubleIconEntranceRight: {
    iconCount: 2,
    iconSlots: [
      { delay: '0.25s', margin: '-0.55rem', nudge: 'hintNudgeTrailing' },
      { delay: '0s', margin: '0', nudge: 'hintNudgeLead' },
    ],
    fadeKeyframeName: 'hintSyncFade',
    backdropAnimation:
      'hintBackdropIn 0.35s ease-out forwards, hintSyncFade 1.2s ease 3s forwards',
    restartPause: 800,
    keyframes: {
      '@keyframes hintPulseIn': {
        '0%': { transform: 'scale(0.85)', opacity: 0 },
        '100%': { transform: 'scale(1)', opacity: 1 },
      },
      '@keyframes hintNudgeLead': {
        '0%': { transform: 'translateX(0)' },
        '45%': { transform: 'translateX(10px)' },
        '50%': { transform: 'translateX(10px)' },
        '90%': { transform: 'translateX(0)' },
        '100%': { transform: 'translateX(0)' },
      },
      '@keyframes hintNudgeTrailing': {
        '0%': { transform: 'translateX(0)' },
        '45%': { transform: 'translateX(20px)' },
        '50%': { transform: 'translateX(20px)' },
        '90%': { transform: 'translateX(0)' },
        '100%': { transform: 'translateX(0)' },
      },
      '@keyframes hintSyncFade': {
        '0%': { opacity: 1 },
        '100%': { opacity: 0 },
      },
    },
    getIconSx: (slot) => ({
      opacity: 0,
      mr: slot.margin,
      animation: `
        hintPulseIn 0.35s ease-out ${slot.delay} forwards,
        ${slot.nudge} 0.8s ease-in-out 3 0.6s,
        hintSyncFade 1.2s ease 3s forwards
      `,
    }),
  },
};

const backdropKeyframes = {
  '@keyframes hintBackdropIn': {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 },
  },
};

// -- Component --

interface StartHereHintProps {
  targetSelector: string;
  color?: string;
  icon?: string;
  animation?: keyof typeof ANIMATIONS;
  loops?: number;
  side?: 'left' | 'right';
  /** Called once, after the last loop has played and the hint is gone. */
  onFinish?: () => void;
  /** Colour of a soft patch behind the arrows that hides whatever they overlap; appears and fades with them. */
  backdrop?: string;
}

const StartHereHint: React.FC<StartHereHintProps> = ({
  targetSelector,
  color = 'red',
  icon = 'chevronright',
  animation = 'doubleIconEntranceRight',
  loops = 3,
  side = 'left',
  onFinish,
  backdrop,
}) => {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [playCount, setPlayCount] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const rafId = useRef<number | null>(null);
  const restartTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const config = ANIMATIONS[animation] ?? ANIMATIONS.doubleIconEntranceRight;

  const updatePosition = () => {
    const target = document.querySelector(targetSelector);
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;
    if (!inViewport) return;

    setPos({
      left: side === 'right' ? rect.right : rect.left,
      top: rect.top + rect.height / 2 + 5,
    });
  };

  const requestPositionUpdate = () => {
    if (rafId.current !== null) return;
    rafId.current = requestAnimationFrame(() => {
      updatePosition();
      rafId.current = null;
    });
  };

  useEffect(() => {
    const timer = setTimeout(updatePosition, 700);

    window.addEventListener('scroll', requestPositionUpdate, true);
    window.addEventListener('resize', requestPositionUpdate);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', requestPositionUpdate, true);
      window.removeEventListener('resize', requestPositionUpdate);
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
      if (restartTimer.current !== null) clearTimeout(restartTimer.current);
    };
  }, [targetSelector, side]);

  const handleAnimationEnd = (e: React.AnimationEvent) => {
    if (e.animationName !== config.fadeKeyframeName) return;

    const nextCount = playCount + 1;
    if (nextCount < loops) {
      restartTimer.current = setTimeout(() => {
        setPlayCount(nextCount);
        setAnimKey((k) => k + 1);
      }, config.restartPause);
      return;
    }
    setPlayCount(nextCount);
    onFinish?.();
  };

  if (!pos || playCount >= loops) return null;

  return (
    <>
      {backdrop && (
        <Box
          key={`backdrop-${animKey}`}
          data-testid="start-here-hint-backdrop"
          sx={{
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: 3,
            left: pos.left,
            top: pos.top,
            width: '7rem',
            height: '3.5rem',
            transform:
              side === 'right'
                ? 'translate(0, -50%)'
                : 'translate(-100%, -50%)',
            background: `linear-gradient(to ${side === 'right' ? 'left' : 'right'}, transparent, ${backdrop} 3rem)`,
            opacity: 0,
            animation: config.backdropAnimation,
            ...backdropKeyframes,
            ...config.keyframes,
          }}
        />
      )}
      <Box
        key={animKey}
        sx={{
          position: 'fixed',
          pointerEvents: 'none',
          zIndex: 3,
          left: pos.left,
          top: pos.top,
          transform:
            side === 'right'
              ? 'translate(5%, -50%) scaleX(-1)'
              : 'translate(-105%, -50%)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))',
          ...config.keyframes,
        }}
      >
        {config.iconSlots.map((slot, i) => {
          const isLast = i === config.iconSlots.length - 1;
          return (
            <Box
              key={i}
              onAnimationEnd={isLast ? handleAnimationEnd : undefined}
              sx={config.getIconSx(slot)}
            >
              {getCustomIcon(icon, '2.5rem', color)}
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default StartHereHint;
